import { FormControlLabel, Radio } from '@mui/material';
import * as React from 'react';

import colors from '../../../config/theme/colors';
import { historyValue, styleDocument } from '../../../config/theme/componentTheme';
import { styled } from '@mui/system';

const validValueToShow = (value?: string | number | null): value is string | number =>
  (!!value || value === 0) && value !== '';

const StyledDiv = styled('div', {
  shouldForwardProp: prop => prop !== 'editing' && prop !== 'compact' && prop !== 'twoColumns',
})(({ editing, compact, twoColumns }: { editing: boolean; compact: boolean; twoColumns?: boolean }) => ({
  marginTop: editing === false ? '' : '-0.9rem',
  '* > &:not(:last-of-type)': {
    marginBottom: editing === false ? '' : compact === true ? '-1.3rem' : '0.5rem',
  },
  width: twoColumns ? '50%' : 'auto',
  float: twoColumns ? 'left' : 'none',
}));

const StyledRadio = styled(Radio)({
  position: 'relative',
  left: '-1rem',
  margin: 0,
});

const StyledLabel = styled('div')({
  fontSize: styleDocument.fontSize,
  color: colors.primaryText,
  margin: '1rem 0',
});

const StyledSubElement = styled('div')({
  margin: '0 0 0 4.2rem',
});

const radioOnChange =
  (onChange: IInputBasics['onChange'], name: string) =>
  (event: { currentTarget: { value: boolean | string } }): void => {
    const value =
      event.currentTarget.value === 'true'
        ? true
        : event.currentTarget.value === 'false'
          ? false
          : event.currentTarget.value;
    onChange && onChange({ [name]: value });
  };

const textElements = (
  value?: string | number | null,
  optionFormatter?: (id: string | number) => React.JSX.Element | string,
): React.JSX.Element => (
  <StyledDiv editing={false} compact={false}>
    {value !== undefined && validValueToShow(value) && optionFormatter ? optionFormatter(value) : value ? value : '-'}
  </StyledDiv>
);

const radioElements = (
  value: IRadio['value'],
  options: IRadio['options'],
  optionFormatter: IRadio['optionFormatter'],
  compact: boolean,
  onChange?: (event: { currentTarget: { value: boolean | string } }) => void,
  disabledOptions?: Array<string | number>,
  optionSpecificElements?: IRadio['optionSpecificElements'],
  style?: IInputBasics['style'],
  horizontal?: boolean,
  color?: IRadio['color'],
  twoColumns?: IRadio['twoColumns'],
): React.JSX.Element => {
  const levelPadding = 3; // rems, how much left padding is added to different levels of options

  return (
    <>
      {Array.isArray(options) &&
        options.map((opt, i) => {
          // Object type option for more customizable options and headers/subheaders
          const optIsObject = typeof opt === 'object';
          const optionValue = optIsObject ? opt.value || '' : opt;
          const optionText = optIsObject ? opt.text || opt.value || '' : opt;

          const subElement =
            typeof optionValue === 'string' && optionSpecificElements?.find(e => Object.keys(e).includes(optionValue));
          const checked = optionValue === value;

          return (
            <StyledDiv
              key={optionValue || i}
              editing={true}
              compact={compact}
              style={{ display: horizontal ? 'inline-block' : undefined }}
              twoColumns={twoColumns}
            >
              <FormControlLabel
                style={
                  style && style.backgroundColor && checked
                    ? {
                        backgroundColor: `${style.backgroundColor}`,
                        borderRadius: '0.5rem',
                        margin: 0,
                        paddingRight: '1rem',
                        paddingLeft: 3,
                        marginLeft: -3,
                      }
                    : {
                        marginLeft: 0,
                        paddingLeft: optIsObject ? opt.level * levelPadding + 'rem' : undefined,
                        cursor: optIsObject && opt.type === 'header' ? 'default' : 'pointer',
                      }
                }
                control={
                  optIsObject && opt.type === 'header' ? (
                    <div></div>
                  ) : (
                    <StyledRadio
                      checked={checked}
                      value={optionValue}
                      onChange={onChange}
                      color={color ? color : 'primary'}
                      disabled={disabledOptions ? disabledOptions.includes(optionValue) : undefined}
                      style={{ left: horizontal ? 0 : undefined }}
                    />
                  )
                }
                label={
                  <StyledLabel
                    style={{
                      color: style && style.backgroundColor && checked ? colors.white : undefined,
                      fontWeight: optIsObject && opt.type === 'header' ? 600 : undefined,
                      ...(optIsObject && opt.style ? opt.style : {}),
                    }}
                  >
                    {optionFormatter ? optionFormatter(optionText) : optionText}
                  </StyledLabel>
                }
              />
              {checked && subElement ? <StyledSubElement>{subElement[optionValue]}</StyledSubElement> : null}
            </StyledDiv>
          );
        })}
    </>
  );
};

const Radios = ({
  editing = false,
  name,
  onChange,
  value,
  options,
  optionFormatter,
  disabledOptions,
  optionSpecificElements,
  style,
  compact = false,
  horizontal = false,
  color,
  twoColumns,
}: IInputBasics & IRadio): React.JSX.Element | React.JSX.Element[] =>
  !editing ? (
    <div
      style={{
        ...historyValue,
        ...{
          color: style && style.color ? `${style.color}` : undefined,
          backgroundColor: style && style.backgroundColor ? `${style.backgroundColor}` : undefined,
          padding: style && style.backgroundColor ? '0 2px 0 2px' : 0,
          borderRadius: style && style.backgroundColor ? '0.5rem' : 0,
          fontWeight: style?.fontWeight ?? historyValue.fontWeight,
        },
      }}
    >
      {textElements(value, optionFormatter)}
    </div>
  ) : (
    <div>
      {radioElements(
        value,
        options,
        optionFormatter,
        compact,
        radioOnChange(onChange, name),
        disabledOptions,
        optionSpecificElements,
        style,
        horizontal,
        color,
        twoColumns,
      )}
    </div>
  );

export default Radios;
